import React from "react";
import Card from "../../components/shared/card";
import Typography from "../../components/shared/typography";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import { Group } from "../utils/formSections";
import FieldRenderer from "./FieldRenderer";
import { Control, FieldErrors } from "react-hook-form";

type Props = {
  groups: Group[];
  chunkIndex: number;
  setChunkIndex: React.Dispatch<React.SetStateAction<number>>;
  control: Control<Record<string, any>>;
  errors: FieldErrors<Record<string, any>>;
  formValues: Record<string, any>;
  trigger: (name?: string | string[]) => Promise<boolean>;
  canContinue: boolean;
  onContinueVerify: () => void;
  showToast: (msg: string, type: any) => void;
};

export function RequestDetailsWizard({
  groups,
  chunkIndex,
  setChunkIndex,
  control,
  errors,
  formValues,
  trigger,
  canContinue,
  onContinueVerify,
  showToast,
}: Props) {
  const group = groups[chunkIndex];
  const names = group?.fields || [];

  return (
    <Card className="mb-8" variant="beige" elevation={1}>
      <Card.Body>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {group?.title && (
            <div className="md:col-span-2">
              <Typography variant="subtitle1" className="text-brown-900">
                {group.title}
              </Typography>
            </div>
          )}
          {names.map((n) => (
            <FieldRenderer
              key={`${n}-${chunkIndex}`}
              name={n}
              control={control}
              errors={errors}
              formValues={formValues}
            />
          ))}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <Typography variant="caption" className="text-brown-600">
            Step {chunkIndex + 1} of {groups.length}
          </Typography>
          <div className="flex gap-2">
            <SubmitButton
              color="cream"
              size="sm"
              onClick={() => setChunkIndex((i) => Math.max(0, i - 1))}
              isDisabled={chunkIndex === 0}
            >
              Back
            </SubmitButton>
            {chunkIndex < groups.length - 1 ? (
              <SubmitButton
                color="terracotta"
                size="sm"
                onClick={async () => {
                  const fields = groups[chunkIndex]?.fields || [];
                  const ok = await trigger(fields as any);
                  if (!ok) {
                    showToast("Please fix the highlighted fields", "error");
                    return;
                  }
                  setChunkIndex((i) => Math.min(groups.length - 1, i + 1));
                }}
              >
                Next
              </SubmitButton>
            ) : (
              <SubmitButton
                color="brown"
                size="sm"
                isDisabled={!canContinue}
                onClick={onContinueVerify}
              >
                Continue to Verify
              </SubmitButton>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default RequestDetailsWizard;

